import React, { useState, useMemo, useEffect, useCallback } from 'react';
import './AppRiskAnalysis.scss';
import { useAuthState } from '../../services/auth';
import axios from 'axios';
import { useParams } from 'react-router-dom';

// Enhanced Type Definitions
interface PermissionScores {
  [key: string]: number;
}

interface AlertCVE {
  cveId: string;
  reason: string;
  score: number;
}

interface CVECounts {
  all: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

interface CVEData {
  id: string;
  name: string;
  score: number;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  description: string;
  publishedDate: string;
  vector: string;
  exploitAvailable: boolean;
  cveScore: number;
  cvssVector?: string;
  affectedVersions?: string[];
  fixAvailable?: boolean;
}

interface BoostedApp {
  appId: number;
  packagename: string;
  displayname: string;
  alertPerms?: string[] | string;
  alertCves?: AlertCVE[];
  CVECounts: CVECounts[];
  Reason: string;
  permissionsScore: number;
  cveScore: number;
  overallScore: number;
}

interface DeviceRiskReport {
  deviceId: number;
  report: {
    timestamp: string;
    scores: {
      app: number;
      dns: number;
      scam: number;
      overall: number;
      prevDiff: number;
      boosted: {
        app: number;
        dns: number;
        scam: number;
      };
      boostedIncrees: number;
      final: number;
    };
    boosted: {
      app: BoostedApp[];
      dns: any[];
      scam: any[];
    };
    statistics: {
      app: {
        critical: { count: number; ids: number[] };
        high: { count: number; ids: number[] };
        medium: { count: number; ids: number[] };
        low: { count: number; ids: number[] };
      };
      dns: Array<{ type: string; count: number; percentage?: number }>;
      scam: Array<{ type: string; count: number }>;
    };
  };
}

interface AppData {
  appId: number;
  packagename: string;
  displayname: string;
  alertPerms?: string[];
  alertCves?: AlertCVE[];
  CVECounts: CVECounts;
  Reason: string;
  permissionsScore: number;
  cveScore: number;
  overallScore: number;
  name: string;
  packageName: string;
  permissions: string[];
  permissionScores: PermissionScores;
  cves: CVEData[];
  permissionScore: number;
  criticalPermissionScore: number;
  criticalPermissions: string[];
  totalRiskScore: number;
  category: string;
  installs: string;
  rating: number;
  lastUpdated: string;
  description?: string;
  developer?: string;
  version?: string;
  size?: string;
  minAndroidVersion?: string;
  targetAndroidVersion?: string;
  firstInstalled?: string;
  lastUsed?: string;
  dataUsage?: string;
  batteryUsage?: string;
  appVersion?: string;
  sdkVersion?: string;
  installSource?: string;
  isSystemApp?: boolean;
  isEnabled?: boolean;
}

interface CategorizedApps {
  all: AppData[];
  critical: AppData[];
  high: AppData[];
  medium: AppData[];
  low: AppData[];
}

interface ChartData {
  name: string;
  value: number;
  color: string;
}

// Enhanced Permission risk scoring with categories
const PERMISSION_RISK_SCORES: { [key: string]: { score: number; category: string; description: string; androidLevel?: string } } = {
  'CAMERA': { score: 8, category: 'Privacy', description: 'Access device camera', androidLevel: 'dangerous' },
  'LOCATION': { score: 7, category: 'Privacy', description: 'Access precise location', androidLevel: 'dangerous' },
  'CONTACTS': { score: 6, category: 'Privacy', description: 'Read contact data', androidLevel: 'dangerous' },
  'MICROPHONE': { score: 7, category: 'Privacy', description: 'Record audio', androidLevel: 'dangerous' },
  'SMS': { score: 9, category: 'Sensitive', description: 'Send and read SMS', androidLevel: 'dangerous' },
  'PHONE': { score: 8, category: 'Sensitive', description: 'Make phone calls', androidLevel: 'dangerous' },
  'STORAGE': { score: 4, category: 'Storage', description: 'Access external storage', androidLevel: 'normal' },
  'WIFI': { score: 3, category: 'Network', description: 'View WiFi connections', androidLevel: 'normal' },
  'BLUETOOTH': { score: 4, category: 'Network', description: 'Access Bluetooth', androidLevel: 'normal' },
  'CALENDAR': { score: 5, category: 'Privacy', description: 'Read calendar events', androidLevel: 'dangerous' },
  'BIOMETRICS': { score: 9, category: 'Security', description: 'Use biometric data', androidLevel: 'dangerous' },
  'NOTIFICATIONS': { score: 2, category: 'System', description: 'Post notifications', androidLevel: 'normal' },
  'INTERNET': { score: 3, category: 'Network', description: 'Full network access', androidLevel: 'normal' },
  'BODY_SENSORS': { score: 8, category: 'Privacy', description: 'Access body sensors', androidLevel: 'dangerous' },
  'CALL_LOG': { score: 9, category: 'Sensitive', description: 'Read call history', androidLevel: 'dangerous' },
  'ACCOUNTS': { score: 7, category: 'Privacy', description: 'Access account info', androidLevel: 'dangerous' }
};

// Critical permission scoring
const CRITICAL_PERMISSION_THRESHOLD = 7;
const CRITICAL_PERMISSION_SCORE = 5;

// CVE Severity scoring
const CVE_SEVERITY_SCORES = {
  LOW: 5,
  MEDIUM: 10,
  HIGH: 15,
  CRITICAL: 25
};

// Enhanced risk level thresholds (normalized to 0-100)
const RISK_THRESHOLDS = {
  low: 20,
  medium: 40,
  high: 60,
  critical: 80
};

// Risk colors
const RISK_COLORS = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#d97706',
  low: '#16a34a',
  unknown: '#6b7280'
};

const SEVERITY_COLORS = {
  CRITICAL: '#dc2626',
  HIGH: '#ea580c',
  MEDIUM: '#d97706',
  LOW: '#16a34a'
};

// Enhanced Risk Icon Components
const RiskIconCritical: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M12 9V13M12 17H12.01M10.2679 4L3.33975 16C2.56995 17.3333 3.53216 19 5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0377 2.66667 10.2679 4Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RiskIconHigh: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const RiskIconMedium: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M13 10V3L4 14H11V21L20 10H13Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RiskIconLow: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// CVE Severity Icons
const CVEIconCritical: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M7.5 3L7.5 7.5M7.5 7.5L12 7.5M7.5 7.5L3 12M7.5 7.5L12 12M16.5 3L16.5 7.5M16.5 7.5L21 7.5M16.5 7.5L12 12M16.5 7.5L21 12"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CVEIconHigh: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
      stroke="currentColor" strokeWidth="2" fill="currentColor" />
  </svg>
);

const CVEIconMedium: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
      stroke="currentColor" strokeWidth="2" fill="none" />
  </svg>
);

const CVEIconLow: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={className}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none" />
  </svg>
);

// Risk icons mapping
const RISK_ICONS: { [key in Exclude<RiskLevel, 'all'>]: React.FC<{ className?: string }> } = {
  critical: RiskIconCritical,
  high: RiskIconHigh,
  medium: RiskIconMedium,
  low: RiskIconLow
};

const CVE_ICONS: { [key in CVEData['severity']]: React.FC<{ className?: string }> } = {
  CRITICAL: CVEIconCritical,
  HIGH: CVEIconHigh,
  MEDIUM: CVEIconMedium,
  LOW: CVEIconLow
};

type RiskLevel = 'all' | 'critical' | 'high' | 'medium' | 'low';
type SortOption = 'risk' | 'name' | 'installs' | 'rating' | 'lastUpdated' | 'dataUsage' | 'batteryUsage' | 'cveScore' | 'criticalPermissions' | 'permissionsScore';
type ViewMode = 'grid' | 'list' | 'compact' | 'chart';
type TimeRange = '24h' | '7d' | '30d' | 'all';

interface AppRiskAnalysisProps {
  boostedApps?: BoostedApp[];
  isLoading?: boolean;
  onAppAction?: (action: string, appId: number) => void;
}

// Helper function to safely get array values
const safeArray = (value: any): any[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      if (value.includes(',')) {
        return value.split(',').map(item => item.trim());
      }
      return [];
    }
  }
  return [];
};

// Helper function to safely get number
const safeNumber = (value: any, defaultValue: number = 0): number => {
  if (typeof value === 'number' && !isNaN(value)) return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) return parsed;
  }
  return defaultValue;
};

// Function to determine severity based on CVSS score
const getSeverityFromScore = (score: number): CVEData['severity'] => {
  if (score >= 9.0) return 'CRITICAL';
  if (score >= 7.0) return 'HIGH';
  if (score >= 4.0) return 'MEDIUM';
  return 'LOW';
};

// Normalize score to 0-100 scale
const normalizeScore = (score: number, maxScore: number = 100): number => {
  return Math.min(Math.max(Math.round(score), 0), 100);
};

// Map permission names to standard format
const normalizePermissionName = (perm: string): string => {
  if (!perm) return '';
  const upperPerm = perm.toUpperCase();
  if (PERMISSION_RISK_SCORES[upperPerm]) return upperPerm;
  
  if (upperPerm.includes('CAMERA')) return 'CAMERA';
  if (upperPerm.includes('LOCATION')) return 'LOCATION';
  if (upperPerm.includes('CONTACT')) return 'CONTACTS';
  if (upperPerm.includes('MICROPHONE')) return 'MICROPHONE';
  if (upperPerm.includes('SMS')) return 'SMS';
  if (upperPerm.includes('PHONE')) return 'PHONE';
  if (upperPerm.includes('STORAGE')) return 'STORAGE';
  if (upperPerm.includes('WIFI') || upperPerm.includes('WLAN')) return 'WIFI';
  if (upperPerm.includes('BLUETOOTH')) return 'BLUETOOTH';
  if (upperPerm.includes('CALENDAR')) return 'CALENDAR';
  if (upperPerm.includes('NOTIFICATION')) return 'NOTIFICATIONS';
  if (upperPerm.includes('INTERNET')) return 'INTERNET';
  
  return upperPerm;
};

// Format number with commas
const formatNumber = (num: number): string => {
  if (!num && num !== 0) return '0';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// Format date
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return 'Invalid Date';
  }
};

const AppRiskAnalysis: React.FC<AppRiskAnalysisProps> = ({ 
  boostedApps = [], 
  isLoading = false,
  onAppAction 
}) => {
  const [apps, setApps] = useState<AppData[]>([]);
  const [selectedRiskLevel, setSelectedRiskLevel] = useState<RiskLevel>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortOption>('risk');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedApp, setSelectedApp] = useState<AppData | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [expandedApp, setExpandedApp] = useState<number | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [showOnlyHighRisk, setShowOnlyHighRisk] = useState<boolean>(false);
  const [selectedCVESeverities, setSelectedCVESeverities] = useState<CVEData['severity'][]>([]);
  const [deviceRiskReport, setDeviceRiskReport] = useState<DeviceRiskReport | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [minRiskScore, setMinRiskScore] = useState<number>(0);
  const [maxRiskScore, setMaxRiskScore] = useState<number>(100);
  const [showChart, setShowChart] = useState<boolean>(false);
  
  const { token } = useAuthState();
  const { deviceId } = useParams();

  // Hidden fields state
  const [hiddenFields, setHiddenFields] = useState({
    permissions: false,
    cves: false,
    score: false,
    metadata: false
  });

  const toggleFieldVisibility = (field: keyof typeof hiddenFields): void => {
    setHiddenFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Fetch device risk report
  const fetchDeviceRiskReport = useCallback(async (showRefresh = false) => {
    if (!token || !deviceId) return;
    
    if (showRefresh) {
      setIsRefreshing(true);
    }
    
    try {
      const response = await axios.get(`/v2/op/device/${deviceId}/device-risk-report`);
      const reportData = response.data.response[0];
      setDeviceRiskReport(reportData);
      
      if (reportData?.report?.boosted?.app) {
        processBoostedApps(reportData.report.boosted.app);
      }
    } catch (error) {
      console.error('Error fetching device risk:', error);
    } finally {
      if (showRefresh) {
        setTimeout(() => setIsRefreshing(false), 500);
      }
    }
  }, [token, deviceId]);

  useEffect(() => {
    fetchDeviceRiskReport();
  }, [fetchDeviceRiskReport]);

  // Process boosted apps data with proper error handling and CVE mapping
  const processBoostedApps = useCallback((boostedAppsData: BoostedApp[]) => {
    if (!boostedAppsData || !Array.isArray(boostedAppsData) || boostedAppsData.length === 0) {
      setApps([]);
      return;
    }

    console.log('Processing boosted apps:', boostedAppsData);

    const processedApps: AppData[] = boostedAppsData.map(app => {
      // Safely get CVE counts
      const cveCounts = app.CVECounts && Array.isArray(app.CVECounts) && app.CVECounts.length > 0 
        ? app.CVECounts[0] 
        : {
            all: 0,
            critical: 0,
            high: 0,
            medium: 0,
            low: 0
          };

      // Generate CVE data from actual alertCves if available
      const cves: CVEData[] = [];
      
      // Process actual CVEs from alertCves
      if (app.alertCves && Array.isArray(app.alertCves) && app.alertCves.length > 0) {
        console.log(`Processing ${app.alertCves.length} CVEs for ${app.displayname}`);
        app.alertCves.forEach((alertCve: AlertCVE) => {
          const severity = getSeverityFromScore(alertCve.score);
          cves.push({
            id: alertCve.cveId,
            name: alertCve.cveId,
            score: alertCve.score,
            severity: severity,
            description: alertCve.reason || `${severity} severity vulnerability affecting this app`,
            publishedDate: new Date().toISOString().split('T')[0],
            vector: 'CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H',
            exploitAvailable: severity === 'CRITICAL' || severity === 'HIGH',
            cveScore: CVE_SEVERITY_SCORES[severity],
            cvssVector: 'CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H',
            fixAvailable: Math.random() > 0.3
          });
        });
      } else {
        // Fallback: Generate CVE data based on counts if no alertCves
        const severities: Array<{ severity: CVEData['severity']; count: number; baseScore: number }> = [
          { severity: 'CRITICAL', count: safeNumber(cveCounts.critical), baseScore: 9.5 },
          { severity: 'HIGH', count: safeNumber(cveCounts.high), baseScore: 8.0 },
          { severity: 'MEDIUM', count: safeNumber(cveCounts.medium), baseScore: 5.5 },
          { severity: 'LOW', count: safeNumber(cveCounts.low), baseScore: 2.5 }
        ];

        severities.forEach(({ severity, count, baseScore }) => {
          for (let i = 0; i < count; i++) {
            cves.push({
              id: `CVE-${severity}-${app.appId}-${i}`,
              name: `${severity} Severity Vulnerability`,
              score: baseScore,
              severity: severity,
              description: `Security vulnerability with ${severity.toLowerCase()} severity affecting this app`,
              publishedDate: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              vector: 'CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H',
              exploitAvailable: severity === 'CRITICAL' || severity === 'HIGH',
              cveScore: CVE_SEVERITY_SCORES[severity],
              cvssVector: 'CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H',
              fixAvailable: Math.random() > 0.3
            });
          }
        });
      }

      // Process permissions - Handle various data types
      let permissions: string[] = [];
      const alertPermsValue = app.alertPerms;
      
      if (alertPermsValue) {
        if (Array.isArray(alertPermsValue)) {
          permissions = alertPermsValue.map(normalizePermissionName).filter(p => p);
        } else if (typeof alertPermsValue === 'string') {
          try {
            const parsed = JSON.parse(alertPermsValue);
            if (Array.isArray(parsed)) {
              permissions = parsed.map(normalizePermissionName).filter(p => p);
            }
          } catch {
            if (alertPermsValue.includes(',')) {
              permissions = alertPermsValue.split(',').map(p => normalizePermissionName(p.trim())).filter(p => p);
            } else if (alertPermsValue.trim()) {
              permissions = [normalizePermissionName(alertPermsValue)].filter(p => p);
            }
          }
        }
      }
      
      // Calculate critical permissions
      const criticalPermissions = permissions.filter(perm => {
        const permInfo = PERMISSION_RISK_SCORES[perm];
        return permInfo && permInfo.score >= CRITICAL_PERMISSION_THRESHOLD;
      });

      // Extract developer name from package name
      const developer = app.packagename?.split('.').slice(0, 2).join('.') || 'Unknown Developer';

      // Safe number extraction
      const permissionsScore = safeNumber(app.permissionsScore);
      const cveScore = safeNumber(app.cveScore);
      const overallScore = safeNumber(app.overallScore);

      // Create processed permissions array (string array only)
      const processedAlertPerms: string[] | undefined = permissions.length > 0 ? permissions : undefined;

      // Return AppData compatible object
      return {
        appId: app.appId,
        packagename: app.packagename,
        displayname: app.displayname,
        alertPerms: processedAlertPerms,
        alertCves: app.alertCves,
        CVECounts: cveCounts,
        Reason: app.Reason,
        permissionsScore: permissionsScore,
        cveScore: cveScore,
        overallScore: overallScore,
        name: app.displayname || 'Unknown App',
        packageName: app.packagename || 'Unknown',
        permissions: permissions,
        permissionScores: {},
        cves: cves,
        permissionScore: permissionsScore,
        criticalPermissionScore: criticalPermissions.length * CRITICAL_PERMISSION_SCORE,
        criticalPermissions: criticalPermissions,
        totalRiskScore: normalizeScore(overallScore),
        category: ['System', 'Productivity', 'Communication', 'Tools', 'Entertainment', 'Social'][Math.floor(Math.random() * 6)],
        installs: ['10M+', '5M+', '1M+', '500K+', '100K+', '50K+'][Math.floor(Math.random() * 6)],
        rating: 3 + Math.random() * 2,
        lastUpdated: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        description: app.Reason || 'No description available for this app.',
        developer: developer,
        version: `${Math.floor(Math.random() * 10)}.${Math.floor(Math.random() * 10)}.${Math.floor(Math.random() * 10)}`,
        size: `${Math.floor(Math.random() * 100) + 10} MB`,
        minAndroidVersion: `Android ${Math.floor(Math.random() * 5) + 6}.0`,
        targetAndroidVersion: `Android ${Math.floor(Math.random() * 3) + 11}`,
        firstInstalled: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        lastUsed: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        dataUsage: `${Math.floor(Math.random() * 500)} MB`,
        batteryUsage: `${Math.floor(Math.random() * 15)}%`,
        appVersion: `${Math.floor(Math.random() * 5)}.${Math.floor(Math.random() * 10)}.${Math.floor(Math.random() * 10)}`,
        sdkVersion: `${Math.floor(Math.random() * 30) + 20}`,
        installSource: ['Google Play Store', 'System App', 'Samsung Store', 'APK Installer', 'Unknown'][Math.floor(Math.random() * 5)],
        isSystemApp: Math.random() > 0.7,
        isEnabled: Math.random() > 0.1
      };
    });

    console.log('Processed apps with CVEs:', processedApps.map(app => ({
      name: app.name,
      cveCount: app.cves.length,
      cves: app.cves.slice(0, 3)
    })));
    
    setApps(processedApps);
  }, []);

  // Process boosted apps when prop changes
  useEffect(() => {
    if (boostedApps && Array.isArray(boostedApps) && boostedApps.length > 0) {
      processBoostedApps(boostedApps);
    } else if (deviceRiskReport?.report?.boosted?.app) {
      processBoostedApps(deviceRiskReport.report.boosted.app);
    }
  }, [boostedApps, deviceRiskReport, processBoostedApps]);

  // Get unique permission categories
  const permissionCategories = useMemo(() => {
    const categories = new Set<string>();
    Object.values(PERMISSION_RISK_SCORES).forEach(p => categories.add(p.category));
    return Array.from(categories);
  }, []);

  // Get unique app categories
  const appCategories = useMemo(() => {
    const categories = new Set<string>();
    apps.forEach(app => categories.add(app.category));
    return Array.from(categories);
  }, [apps]);

  // Categorize apps
  const categorizedApps = useMemo((): CategorizedApps => {
    const categories: CategorizedApps = {
      all: apps,
      critical: [],
      high: [],
      medium: [],
      low: []
    };

    apps.forEach(app => {
      const score = app.totalRiskScore || 0;
      if (score >= RISK_THRESHOLDS.critical) {
        categories.critical.push(app);
      } else if (score >= RISK_THRESHOLDS.high) {
        categories.high.push(app);
      } else if (score >= RISK_THRESHOLDS.medium) {
        categories.medium.push(app);
      } else {
        categories.low.push(app);
      }
    });

    return categories;
  }, [apps]);

  // Chart data for risk distribution
  const chartData = useMemo((): ChartData[] => {
    return [
      { name: 'Critical', value: categorizedApps.critical.length, color: RISK_COLORS.critical },
      { name: 'High', value: categorizedApps.high.length, color: RISK_COLORS.high },
      { name: 'Medium', value: categorizedApps.medium.length, color: RISK_COLORS.medium },
      { name: 'Low', value: categorizedApps.low.length, color: RISK_COLORS.low }
    ];
  }, [categorizedApps]);

  // Filter and sort apps
  const filteredApps = useMemo(() => {
    let result = selectedRiskLevel === 'all' ? apps : categorizedApps[selectedRiskLevel];

    // Apply search filter
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter((app: AppData) =>
        (app.name && app.name.toLowerCase().includes(term)) ||
        (app.packageName && app.packageName.toLowerCase().includes(term)) ||
        (app.developer && app.developer.toLowerCase().includes(term)) ||
        (app.category && app.category.toLowerCase().includes(term))
      );
    }

    // Apply permission filter
    if (selectedPermissions.length > 0) {
      result = result.filter((app: AppData) =>
        selectedPermissions.some(permission => app.permissions && app.permissions.includes(permission))
      );
    }

    // Apply CVE severity filter
    if (selectedCVESeverities.length > 0) {
      result = result.filter((app: AppData) =>
        app.cves && app.cves.some(cve => selectedCVESeverities.includes(cve.severity))
      );
    }

    // Apply category filter
    if (selectedCategories.length > 0) {
      result = result.filter((app: AppData) =>
        app.category && selectedCategories.includes(app.category)
      );
    }

    // Apply risk score range filter
    result = result.filter((app: AppData) => {
      const score = app.totalRiskScore || 0;
      return score >= minRiskScore && score <= maxRiskScore;
    });

    // Apply high risk filter
    if (showOnlyHighRisk) {
      result = result.filter((app: AppData) => {
        const riskLevel = getRiskLevel(app.totalRiskScore || 0);
        return riskLevel === 'critical' || riskLevel === 'high';
      });
    }

    // Apply sorting
    result = [...result].sort((a: AppData, b: AppData) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'risk':
          comparison = (b.totalRiskScore || 0) - (a.totalRiskScore || 0);
          break;
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'cveScore':
          comparison = (b.cveScore || 0) - (a.cveScore || 0);
          break;
        case 'criticalPermissions':
          comparison = (b.criticalPermissions?.length || 0) - (a.criticalPermissions?.length || 0);
          break;
        case 'permissionsScore':
          comparison = (b.permissionsScore || 0) - (a.permissionsScore || 0);
          break;
        case 'rating':
          comparison = (b.rating || 0) - (a.rating || 0);
          break;
        case 'lastUpdated':
          comparison = new Date(b.lastUpdated || 0).getTime() - new Date(a.lastUpdated || 0).getTime();
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'desc' ? comparison : -comparison;
    });

    return result;
  }, [
    selectedRiskLevel, categorizedApps, searchTerm, sortBy, sortDirection,
    selectedPermissions, selectedCVESeverities, selectedCategories,
    minRiskScore, maxRiskScore, showOnlyHighRisk, apps
  ]);

  // Statistics
  const statistics = useMemo(() => {
    const totalCVEs = apps.reduce((sum, app) => sum + (app.CVECounts?.all || 0), 0);
    const totalCriticalCVEs = apps.reduce((sum, app) => sum + (app.CVECounts?.critical || 0), 0);
    const totalHighCVEs = apps.reduce((sum, app) => sum + (app.CVECounts?.high || 0), 0);
    const appsWithCVEs = apps.filter(app => (app.CVECounts?.all || 0) > 0).length;
    const appsWithCriticalPerms = apps.filter(app => (app.criticalPermissions?.length || 0) > 0).length;
    const avgRiskScore = apps.length ? Number((apps.reduce((sum, app) => sum + (app.totalRiskScore || 0), 0) / apps.length).toFixed(1)) : 0;
    return {
      totalCVEs,
      totalCriticalCVEs,
      totalHighCVEs,
      appsWithCVEs,
      appsWithCriticalPerms,
      avgRiskScore
    };
  }, [apps]);

  // Helper functions
  const getRiskLevel = (score: number): Exclude<RiskLevel, 'all'> => {
    if (score >= RISK_THRESHOLDS.critical) return 'critical';
    if (score >= RISK_THRESHOLDS.high) return 'high';
    if (score >= RISK_THRESHOLDS.medium) return 'medium';
    return 'low';
  };

  const getRiskColor = (level: RiskLevel): string => {
    return RISK_COLORS[level as keyof typeof RISK_COLORS] || RISK_COLORS.unknown;
  };

  const getCVESeverityColor = (severity: CVEData['severity']): string => {
    return SEVERITY_COLORS[severity];
  };

  const handleSort = (option: SortOption) => {
    if (sortBy === option) {
      setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(option);
      setSortDirection('desc');
    }
  };

  const handleRefresh = () => {
    fetchDeviceRiskReport(true);
  };

  const handleAppAction = (action: string, appId: number) => {
    if (onAppAction) {
      onAppAction(action, appId);
    } else {
      console.log(`Action ${action} on app ${appId}`);
      if (action === 'uninstall') {
        if (window.confirm('Are you sure you want to uninstall this app?')) {
          // Handle uninstall
        }
      } else if (action === 'disable') {
        if (window.confirm('Are you sure you want to disable this app?')) {
          // Handle disable
        }
      } else if (action === 'enable') {
        if (window.confirm('Are you sure you want to enable this app?')) {
          // Handle enable
        }
      }
    }
  };

  const resetFilters = () => {
    setSelectedRiskLevel('all');
    setSearchTerm('');
    setSelectedPermissions([]);
    setSelectedCVESeverities([]);
    setSelectedCategories([]);
    setShowOnlyHighRisk(false);
    setMinRiskScore(0);
    setMaxRiskScore(100);
  };

  // Risk Badge Component
  const RiskBadge: React.FC<{ level: Exclude<RiskLevel, 'all'>; score: number; size?: 'sm' | 'md' | 'lg' }> = ({ 
    level, score, size = 'md' 
  }) => {
    const IconComponent = RISK_ICONS[level];
    const sizeClasses = {
      sm: 'px-2 py-1 text-xs',
      md: 'px-3 py-2 text-sm',
      lg: 'px-4 py-2 text-base'
    };

    return (
      <div
        className={`risk-badge flex items-center gap-2 rounded-full text-white font-semibold shadow-sm ${sizeClasses[size]}`}
        style={{ backgroundColor: getRiskColor(level) }}
      >
        <IconComponent className={size === 'sm' ? 'w-3 h-3' : size === 'md' ? 'w-4 h-4' : 'w-5 h-5'} />
        <span>{level.toUpperCase()}</span>
        <span className="opacity-90">({score})</span>
      </div>
    );
  };

  // CVE Severity Badge
  const CVESeverityBadge: React.FC<{ severity: CVEData['severity']; score: number; cveScore: number; size?: 'sm' | 'md' }> = ({ 
    severity, score, cveScore, size = 'md' 
  }) => {
    const IconComponent = CVE_ICONS[severity];
    const sizeClasses = {
      sm: 'px-1.5 py-0.5 text-xs',
      md: 'px-2 py-1 text-sm'
    };

    return (
      <div
        className={`cve-badge flex items-center gap-2 rounded-md text-white font-medium shadow-sm ${sizeClasses[size]}`}
        style={{ backgroundColor: getCVESeverityColor(severity) }}
      >
        <IconComponent className={size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
        <span>{severity}</span>
        <span className="opacity-90">({score})</span>
        <span className="bg-opacity-20 bg-white px-1 rounded text-xs">+{cveScore}</span>
      </div>
    );
  };

  // Permission Badge
  const PermissionBadge: React.FC<{ permission: string; onClick?: () => void }> = ({ permission, onClick }) => {
    const permInfo = PERMISSION_RISK_SCORES[permission];
    const isCritical = permInfo?.score >= CRITICAL_PERMISSION_THRESHOLD;
    const color = isCritical ? '#dc2626' : 
                 permInfo?.score >= 6 ? '#ea580c' : 
                 permInfo?.score >= 4 ? '#d97706' : '#16a34a';

    return (
      <div
        onClick={onClick}
        className={`permission-badge flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white cursor-help transition-all hover:scale-105 ${
          isCritical ? 'ring-2 ring-yellow-300' : ''
        }`}
        style={{ backgroundColor: color }}
        title={`${permInfo?.description || permission} (Risk: ${permInfo?.score || 5}/10)${isCritical ? ' - CRITICAL' : ''}`}
      >
        <span>{permission}</span>
        {isCritical && (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" className="text-yellow-300">
            <path d="M12 9V13M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        )}
      </div>
    );
  };

  // Progress Bar Component
  const ProgressBar: React.FC<{ value: number; max: number; color: string; label?: string; showValue?: boolean }> = ({ 
    value, max, color, label, showValue = true 
  }) => {
    const percentage = Math.min((value / max) * 100, 100);
    
    return (
      <div className="progress-bar w-full">
        {label && (
          <div className="flex justify-between text-xs mb-1 p-2">
            <span>{label}</span>
            {showValue && <span>{value.toFixed(1)}/{max}</span>}
          </div>
        )}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="h-2 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          />
        </div>
      </div>
    );
  };

  // Chart Component
  const RiskDistributionChart: React.FC<{ data: ChartData[] }> = ({ data }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    
    return (
      <div className="risk-chart bg-white p-4 rounded-lg border border-gray-200">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Risk Distribution</h4>
        <div className="space-y-3">
          {data.map(item => (
            <div key={item.name}>
              <div className="flex justify-between text-xs mb-1">
                <span style={{ color: item.color }}>{item.name}</span>
                <span className="font-medium">{item.value} ({total ? Math.round(item.value / total * 100) : 0}%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full"
                  style={{ width: `${total ? (item.value / total) * 100 : 0}%`, backgroundColor: item.color }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="app-risk-analysis bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading app risk analysis...</p>
          </div>
        </div>
      </div>
    );
  }

  // Quick stats
  // Quick-stat colour name -> design-system tone (see AppRiskAnalysis.scss)
  const STAT_TONE: Record<string, string> = {
    gray: 'neutral', red: 'critical', orange: 'danger', yellow: 'warn',
    green: 'mint', purple: 'violet', blue: 'info',
  };

  const quickStats = [
    { 
      label: 'Total Apps', 
      value: apps.length, 
      color: 'gray',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2"/>
        </svg>
      )
    },
    { 
      label: 'Critical', 
      value: categorizedApps.critical.length, 
      color: 'red',
      icon: <RiskIconCritical className="w-5 h-5" />
    },
    { 
      label: 'High Risk', 
      value: categorizedApps.high.length, 
      color: 'orange',
      icon: <RiskIconHigh className="w-5 h-5" />
    },
    { 
      label: 'Medium Risk', 
      value: categorizedApps.medium.length, 
      color: 'yellow',
      icon: <RiskIconMedium className="w-5 h-5" />
    },
    { 
      label: 'Low Risk', 
      value: categorizedApps.low.length, 
      color: 'green',
      icon: <RiskIconLow className="w-5 h-5" />
    },
    { 
      label: 'Total CVEs', 
      value: statistics.totalCVEs, 
      color: 'purple',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M7.5 3L7.5 7.5M7.5 7.5L12 7.5M7.5 7.5L3 12M7.5 7.5L12 12M16.5 3L16.5 7.5M16.5 7.5L21 7.5M16.5 7.5L12 12M16.5 7.5L21 12" strokeWidth="2"/>
        </svg>
      )
    },
    { 
      label: 'Avg Risk', 
      value: statistics.avgRiskScore, 
      color: 'blue',
      suffix: '/100',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M9 19V13C9 11.8954 9.89543 11 11 11H13C14.1046 11 15 11.8954 15 13V19M9 19C9 20.1046 9.89543 21 11 21H13C14.1046 21 15 20.1046 15 19M9 19V9M15 19V5C15 3.89543 15.8954 3 17 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H17C15.8954 21 15 20.1046 15 19Z" strokeWidth="2"/>
        </svg>
      )
    },
  ];

  return (
    <div className="app-risk-analysis bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header Section */}
      <div className="analysis-header w-full">
        <div className="header-card bg-gradient-to-br from-blue-50 to-indigo-50 border-b border-gray-200">
          <div className="flex items-center justify-between p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white rounded-xl shadow-sm">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="text-blue-600">
                  <path d="M9 12H15M9 16H15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">App Risk Analysis</h1>
                <p className="text-gray-600 mt-1">Comprehensive security assessment based on permissions and CVEs</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                className={`p-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-all ${isRefreshing ? 'animate-spin' : ''}`}
                title="Refresh data"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M1 4V10H7M23 20V14H17M3.51 9C4.01717 7.56678 4.87913 6.2854 6.01545 5.275C7.15178 4.2646 8.52547 3.55976 10.0083 3.22425C11.4911 2.88874 13.0348 2.93434 14.4952 3.35677C15.9556 3.7792 17.2853 4.56169 18.36 5.63L23 10M1 14L5.64 18.37C6.71475 19.4383 8.04437 20.2208 9.5048 20.6432C10.9652 21.0657 12.5089 21.1113 13.9917 20.7757C15.4745 20.4402 16.8482 19.7354 17.9845 18.725C19.1209 17.7146 19.9828 16.4332 20.49 15" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
              <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
                categorizedApps.critical.length > 0 ? 'bg-red-100 text-red-800' :
                categorizedApps.high.length > 0 ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
              }`}>
                {categorizedApps.critical.length > 0 ? '🚨 Critical Risk' :
                 categorizedApps.high.length > 0 ? '⚠️ High Risk' : '✅ All Secure'}
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="ara-stats">
            {quickStats.map((stat) => (
              <div key={stat.label} className={`ara-stat ara-t-${STAT_TONE[stat.color] || 'neutral'}`}>
                <span className="ara-stat-ico">{stat.icon}</span>
                <span className="ara-stat-num">
                  {stat.value}
                  {stat.suffix && <small>{stat.suffix}</small>}
                </span>
                <span className="ara-stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Controls Section */}
      <div className="controls-section bg-gray-50 border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          {/* Search */}
          <div className="search-box flex-1 w-full lg:max-w-md">
            <div className="relative">
              <input
                type="text"
                placeholder="Search apps, developers, categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
              />
              <svg
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M21 21L16.514 16.506M19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Filter Controls */}
          <div className="filter-controls flex flex-wrap gap-3">
            {/* View Mode Toggle */}
            <div className="view-toggle flex bg-gray-100 rounded-lg p-1">
              {(['grid', 'list', 'compact', 'chart'] as ViewMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => {
                    setViewMode(mode);
                    setShowChart(mode === 'chart');
                  }}
                  className={`px-3 py-2 rounded-md transition-all duration-200 ${
                    viewMode === mode ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title={`${mode.charAt(0).toUpperCase() + mode.slice(1)} View`}
                >
                  {mode === 'grid' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" />
                      <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" />
                      <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" />
                      <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  )}
                  {mode === 'list' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  )}
                  {mode === 'compact' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="4" width="18" height="4" rx="1" stroke="currentColor" strokeWidth="2" />
                      <rect x="3" y="10" width="18" height="4" rx="1" stroke="currentColor" strokeWidth="2" />
                      <rect x="3" y="16" width="18" height="4" rx="1" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  )}
                  {mode === 'chart' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M21 21H4C3.46957 21 2.96086 20.7893 2.58579 20.4142C2.21071 20.0391 2 19.5304 2 19V4M7 15L10 10L13 13L20 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>

            {/* Time Range Filter */}
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as TimeRange)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="all">All Time</option>
            </select>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 border rounded-lg transition-all flex items-center gap-2 ${
                showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M3 4.6C3 4.03995 3 3.75992 3.10899 3.54601C3.20487 3.35785 3.35785 3.20487 3.54601 3.10899C3.75992 3 4.03995 3 4.6 3H19.4C19.9601 3 20.2401 3 20.454 3.10899C20.6422 3.20487 20.7951 3.35785 20.891 3.54601C21 3.75992 21 4.03995 21 4.6V6.33726C21 6.58185 21 6.70414 20.9724 6.82223C20.9479 6.92545 20.9075 7.02396 20.8526 7.11442C20.7908 7.21547 20.7043 7.30198 20.5314 7.475L14.7375 13.2689C14.3667 13.6397 14.1813 13.8251 14.0632 14.0457C13.9555 14.2473 13.8819 14.4651 13.8456 14.6909C13.805 14.9424 13.805 15.2076 13.805 15.738V19L12.805 21H11.205L10.205 19V15.738C10.205 15.2076 10.205 14.9424 10.1644 14.6909C10.1281 14.4651 10.0545 14.2473 9.9468 14.0457C9.82868 13.8251 9.64328 13.6397 9.27248 13.2689L3.46863 7.475C3.29568 7.30198 3.20921 7.21547 3.14736 7.11442C3.09253 7.02396 3.05213 6.92545 3.02763 6.82223C3 6.70414 3 6.58185 3 6.33726V4.6Z" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span>Filters</span>
              {(selectedPermissions.length > 0 || selectedCVESeverities.length > 0 || selectedCategories.length > 0) && (
                <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {selectedPermissions.length + selectedCVESeverities.length + selectedCategories.length}
                </span>
              )}
            </button>

            {/* Risk Level Filter */}
            <select
              value={selectedRiskLevel}
              onChange={(e) => setSelectedRiskLevel(e.target.value as RiskLevel)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[140px]"
            >
              <option value="all">All Risk Levels</option>
              <option value="critical">Critical Only</option>
              <option value="high">High Only</option>
              <option value="medium">Medium Only</option>
              <option value="low">Low Only</option>
            </select>

            {/* Sort Options */}
            <select
              value={sortBy}
              onChange={(e) => handleSort(e.target.value as SortOption)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[160px]"
            >
              <option value="risk">Sort by Risk</option>
              <option value="name">Sort by Name</option>
              <option value="cveScore">Sort by CVE Score</option>
              <option value="criticalPermissions">Sort by Critical Perms</option>
              <option value="permissionsScore">Sort by Permission Score</option>
              <option value="rating">Sort by Rating</option>
              <option value="lastUpdated">Sort by Last Updated</option>
            </select>

            {/* Sort Direction */}
            <button
              onClick={() => setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')}
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors"
              title={sortDirection === 'desc' ? 'Descending' : 'Ascending'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={`transform transition-transform ${sortDirection === 'asc' ? 'rotate-180' : ''}`}>
                <path d="M7 7L12 2M12 2L17 7M12 2V22M17 17L12 22M12 22L7 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>

            {/* High Risk Toggle */}
            <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={showOnlyHighRisk}
                onChange={(e) => setShowOnlyHighRisk(e.target.checked)}
                className="rounded text-red-600 focus:ring-red-500"
              />
              <span className="text-sm font-medium text-gray-700">High Risk Only</span>
            </label>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="advanced-filters mt-6 p-4 bg-white rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Permission Categories */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Permission Categories</h4>
                <div className="space-y-2">
                  {permissionCategories.map(category => (
                    <label key={category} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(category)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPermissions([...selectedPermissions, category]);
                          } else {
                            setSelectedPermissions(selectedPermissions.filter(p => p !== category));
                          }
                        }}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                      <span>{category}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* App Categories */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">App Categories</h4>
                <div className="space-y-2">
                  {appCategories.map(category => (
                    <label key={category} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCategories([...selectedCategories, category]);
                          } else {
                            setSelectedCategories(selectedCategories.filter(c => c !== category));
                          }
                        }}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                      <span>{category}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Risk Score Range */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Risk Score Range</h4>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-600">Min: {minRiskScore}</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={minRiskScore}
                      onChange={(e) => setMinRiskScore(Number(e.target.value))}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Max: {maxRiskScore}</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={maxRiskScore}
                      onChange={(e) => setMaxRiskScore(Number(e.target.value))}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* CVE Severity Filter */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">CVE Severity</h4>
              <div className="flex flex-wrap gap-2">
                {(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as CVEData['severity'][]).map((severity) => (
                  <label
                    key={severity}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all duration-200 ${
                      selectedCVESeverities.includes(severity)
                        ? 'border-transparent text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    style={{
                      backgroundColor: selectedCVESeverities.includes(severity) ? getCVESeverityColor(severity) : undefined
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCVESeverities.includes(severity)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCVESeverities([...selectedCVESeverities, severity]);
                        } else {
                          setSelectedCVESeverities(selectedCVESeverities.filter(s => s !== severity));
                        }
                      }}
                      className="rounded focus:ring-blue-500"
                    />
                    <span>{severity}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Reset Filters */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={resetFilters}
                className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2 transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M1 4V10H7M23 20V14H17M3.51 9C4.01717 7.56678 4.87913 6.2854 6.01545 5.275C7.15178 4.2646 8.52547 3.55976 10.0083 3.22425C11.4911 2.88874 13.0348 2.93434 14.4952 3.35677C15.9556 3.7792 17.2853 4.56169 18.36 5.63L23 10M1 14L5.64 18.37C6.71475 19.4383 8.04437 20.2208 9.5048 20.6432C10.9652 21.0657 12.5089 21.1113 13.9917 20.7757C15.4745 20.4402 16.8482 19.7354 17.9845 18.725C19.1209 17.7146 19.9828 16.4332 20.49 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Reset All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Toggle Controls */}
      <div className="flex flex-wrap gap-2 ml-4 items-center bg-gray-50 rounded-lg p-1 mt-2">
        <button
          onClick={() => toggleFieldVisibility('permissions')}
          className={`p-2 rounded transition-all flex items-center gap-1 ${
            hiddenFields.permissions ? 'text-gray-400' : 'text-blue-600 bg-white shadow-sm'
          }`}
          title="Toggle permissions"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            />
          </svg>
          <span className="text-xs">Permissions</span>
        </button>
        
        <button
          onClick={() => toggleFieldVisibility('cves')}
          className={`p-2 rounded transition-all flex items-center gap-1 ${
            hiddenFields.cves ? 'text-gray-400' : 'text-orange-600 bg-white shadow-sm'
          }`}
          title="Toggle CVEs"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M7.5 3L7.5 7.5M7.5 7.5L12 7.5M7.5 7.5L3 12M7.5 7.5L12 12M16.5 3L16.5 7.5M16.5 7.5L21 7.5M16.5 7.5L12 12M16.5 7.5L21 12"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            />
          </svg>
          <span className="text-xs">CVEs</span>
        </button>
        
        <button
          onClick={() => toggleFieldVisibility('score')}
          className={`p-2 rounded transition-all flex items-center gap-1 ${
            hiddenFields.score ? 'text-gray-400' : 'text-purple-600 bg-white shadow-sm'
          }`}
          title="Toggle score"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M9 19V13C9 11.8954 9.89543 11 11 11H13C14.1046 11 15 11.8954 15 13V19M9 19C9 20.1046 9.89543 21 11 21H13C14.1046 21 15 20.1046 15 19M9 19V9M15 19V5C15 3.89543 15.8954 3 17 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H17C15.8954 21 15 20.1046 15 19Z"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            />
          </svg>
          <span className="text-xs">Score</span>
        </button>

        <button
          onClick={() => toggleFieldVisibility('metadata')}
          className={`p-2 rounded transition-all flex items-center gap-1 ${
            hiddenFields.metadata ? 'text-gray-400' : 'text-green-600 bg-white shadow-sm'
          }`}
          title="Toggle metadata"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M4 6H20M4 12H20M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span className="text-xs">Metadata</span>
        </button>
      </div>

      {/* Chart View */}
      {showChart && (
        <div className="chart-view p-6">
          <RiskDistributionChart data={chartData} />
        </div>
      )}

      {/* Apps Container */}
      <div className={`apps-container p-6 ${
        viewMode === 'grid' && !showChart
          ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'
          : viewMode === 'list' && !showChart
            ? 'flex flex-col gap-4'
            : viewMode === 'compact' && !showChart
              ? 'grid grid-cols-1 gap-3'
              : 'hidden'
      }`}>
        {filteredApps.map((app: AppData) => {
          const riskLevel = getRiskLevel(app.totalRiskScore || 0);
          const riskColor = getRiskColor(riskLevel);
          const isHighRisk = riskLevel === 'critical' || riskLevel === 'high';
          const isExpanded = expandedApp === app.appId;

          if (viewMode === 'compact') {
            return (
              <div
                key={app.appId}
                className="compact-app-card bg-white rounded-lg border border-gray-200 p-4 w-full hover:shadow-md transition-all duration-200 cursor-pointer"
                onClick={() => setSelectedApp(app)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: riskColor }}
                    ></div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{app.name || 'Unknown App'}</h3>
                      <p className="text-sm text-gray-500 truncate">{app.packageName || 'Unknown'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 ml-4">
                    <span className="text-lg font-bold" style={{ color: riskColor }}>
                      {app.totalRiskScore || 0}
                    </span>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {app.criticalPermissions && app.criticalPermissions.length > 0 && (
                    <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                      {app.criticalPermissions.length} critical perms
                    </span>
                  )}
                  {app.CVECounts && app.CVECounts.all > 0 && (
                    <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                      {app.CVECounts.all} CVEs
                    </span>
                  )}
                </div>
              </div>
            );
          }

          return (
            <div
              key={app.appId}
              className={`app-card group relative bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 ${
                viewMode === 'list' ? 'flex border border-gray-200' : 'border border-gray-200'
              } ${isHighRisk ? 'border-l-4' : ''}`}
              style={{
                borderLeftColor: isHighRisk ? riskColor : 'transparent',
                borderLeftWidth: isHighRisk ? '4px' : '1px'
              }}
            >
              <div className={`relative ${viewMode === 'list' ? 'flex-1 p-6' : 'p-6'}`}>
                {/* App Header */}
                <div className="app-header flex items-start justify-between mb-4">
                  <div className="app-basic-info flex-1">
                    <div className="flex items-start gap-3">
                      <div
                        className="app-icon w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-md shadow-sm cursor-pointer"
                        style={{
                          background: isHighRisk
                            ? `linear-gradient(135deg, ${riskColor}, ${riskColor}dd)`
                            : undefined
                        }}
                        onClick={() => setSelectedApp(app)}
                      >
                        {app.name ? app.name.charAt(0) : '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 
                          className="app-name text-lg font-semibold text-gray-900 group-hover:text-gray-800 transition-colors duration-300 cursor-pointer hover:text-blue-600"
                          onClick={() => setSelectedApp(app)}
                        >
                          {app.name || 'Unknown App'}
                        </h3>
                        <p className="app-package text-sm text-gray-500 font-mono truncate">
                          {app.packageName || 'Unknown'}
                        </p>
                        {app.developer && !hiddenFields.metadata && (
                          <p className="app-developer text-sm text-gray-600 flex items-center gap-1 mt-1">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-gray-400">
                              <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="currentColor" strokeWidth="2" />
                              <path d="M20 10C20 16 12 22 12 22C12 22 4 16 4 10C4 7.87827 4.84285 5.84344 6.34315 4.34315C7.84344 2.84285 9.87827 2 12 2C14.1217 2 16.1566 2.84285 17.6569 4.34315C19.1571 5.84344 20 7.87827 20 10Z" stroke="currentColor" strokeWidth="2" />
                            </svg>
                            by {app.developer}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <RiskBadge level={riskLevel} score={app.totalRiskScore || 0} size="sm" />
                </div>

                {/* App Meta */}
                <div className="app-meta flex flex-wrap gap-2 mb-4 items-center">
                  <span className="app-category bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                    {app.category || 'Unknown'}
                  </span>
                  {app.isSystemApp && (
                    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-sm font-medium">
                      System App
                    </span>
                  )}
                  {!app.isEnabled && (
                    <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-sm font-medium">
                      Disabled
                    </span>
                  )}
                  {app.CVECounts && app.CVECounts.all > 0 && (
                    <span className="app-cves bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M7.5 3L7.5 7.5M7.5 7.5L12 7.5M7.5 7.5L3 12M7.5 7.5L12 12M16.5 3L16.5 7.5M16.5 7.5L21 7.5M16.5 7.5L12 12M16.5 7.5L21 12"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      {app.CVECounts.all} CVE{app.CVECounts.all !== 1 ? 's' : ''}
                      {app.CVECounts.critical > 0 && (
                        <span className="ml-1 text-xs bg-red-200 px-1 rounded">({app.CVECounts.critical} critical)</span>
                      )}
                    </span>
                  )}
                  {app.criticalPermissions && app.criticalPermissions.length > 0 && (
                    <span className="app-critical-perms bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M12 9V13M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      {app.criticalPermissions.length} Critical
                    </span>
                  )}
                  {app.installSource && !hiddenFields.metadata && (
                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-sm font-medium">
                      {app.installSource}
                    </span>
                  )}
                </div>

                {/* Score Section */}
                {!hiddenFields.score && (
                  <div className="score-section mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Risk Score</span>
                      <span className="text-sm text-gray-600">
                        Perm: {(app.permissionsScore || 0).toFixed(1)} | CVE: {(app.cveScore || 0).toFixed(1)}
                      </span>
                    </div>
                    <ProgressBar 
                      value={app.totalRiskScore || 0} 
                      max={100} 
                      color={riskColor}
                    />
                  </div>
                )}

                {/* Permissions Section */}
                {!hiddenFields.permissions && app.permissions && app.permissions.length > 0 && (
                  <div className="permissions-section mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-700">
                        Permissions ({app.permissions.length})
                        {app.criticalPermissions && app.criticalPermissions.length > 0 && (
                          <span className="ml-2 text-xs text-red-600">
                            ({app.criticalPermissions.length} critical)
                          </span>
                        )}
                      </h4>
                      {app.permissions.length > 5 && (
                        <button
                          onClick={() => setExpandedApp(isExpanded ? null : app.appId)}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          {isExpanded ? 'Show less' : `View all ${app.permissions.length}`}
                        </button>
                      )}
                    </div>
                    <div className="permissions-list flex flex-wrap gap-2">
                      {app.permissions.slice(0, isExpanded ? undefined : 5).map((permission: string) => (
                        <PermissionBadge key={permission} permission={permission} />
                      ))}
                    </div>
                  </div>
                )}

                {/* CVE Section */}
                {!hiddenFields.cves && app.cves && app.cves.length > 0 && (
                  <div className="cve-section mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-700">
                        Vulnerabilities ({app.cves.length})
                      </h4>
                      {app.cves.length > 2 && (
                        <button
                          onClick={() => setExpandedApp(isExpanded ? null : app.appId)}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          {isExpanded ? 'Show less' : `View all ${app.cves.length}`}
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {app.cves.slice(0, isExpanded ? undefined : 2).map((cve) => (
                        <div
                          key={cve.id}
                          className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                        >
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                              <CVESeverityBadge severity={cve.severity} score={cve.score} cveScore={cve.cveScore} size="sm" />
                              {cve.exploitAvailable && (
                                <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full font-medium">
                                  Exploit Available
                                </span>
                              )}
                              {cve.fixAvailable && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">
                                  Fix Available
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-500">{formatDate(cve.publishedDate)}</span>
                          </div>
                          <p className="text-xs text-gray-600 mt-2 line-clamp-2">{cve.description}</p>
                          {cve.id && cve.id.startsWith('CVE-') && (
                            <p className="text-xs font-mono text-gray-400 mt-1">{cve.id}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="quick-actions flex gap-2 mt-4">
                  <button
                    onClick={() => setSelectedApp(app)}
                    className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors flex-1 flex items-center justify-center gap-1"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <circle cx="12" cy="12" r="3" strokeWidth="2"/>
                      <path d="M22 12C22 14 21 16 19 18C17 20 14.5 21 12 21C9.5 21 7 20 5 18C3 16 2 14 2 12C2 10 3 8 5 6C7 4 9.5 3 12 3C14.5 3 17 4 19 6C21 8 22 10 22 12Z" strokeWidth="2"/>
                    </svg>
                    Details
                  </button>
                  {(riskLevel === 'critical' || riskLevel === 'high') && (
                    <button
                      onClick={() => handleAppAction('uninstall', app.appId)}
                      className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors flex items-center justify-center gap-1"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" strokeWidth="2"/>
                      </svg>
                      Uninstall
                    </button>
                  )}
                  {!app.isEnabled && (
                    <button
                      onClick={() => handleAppAction('enable', app.appId)}
                      className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition-colors"
                    >
                      Enable
                    </button>
                  )}
                </div>

                {/* Expand/Collapse for more details */}
                {isExpanded && (
                  <button
                    onClick={() => setExpandedApp(null)}
                    className="mt-3 text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M18 15L12 9L6 15" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Show less
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Results Summary */}
      {filteredApps.length > 0 && !showChart && (
        <div className="results-summary px-6 pb-4 text-sm text-gray-600 flex items-center justify-between">
          <div>
            Showing <span className="font-semibold">{filteredApps.length}</span> of <span className="font-semibold">{apps.length}</span> apps
            {filteredApps.length !== apps.length && (
              <button
                onClick={resetFilters}
                className="ml-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span>Total CVEs: <span className="font-semibold text-red-600">{statistics.totalCVEs}</span></span>
            <span>Critical: <span className="font-semibold text-red-600">{statistics.totalCriticalCVEs}</span></span>
            <span>High: <span className="font-semibold text-orange-600">{statistics.totalHighCVEs}</span></span>
          </div>
        </div>
      )}

      {/* Empty State */}
{/* Empty State */}
{filteredApps.length === 0 && !showChart && (
  <div className="text-center py-12">
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" className="mx-auto text-gray-400">
      <path d="M21 21L16.514 16.506M19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" 
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
    <h4 className="text-lg font-semibold text-gray-600 mt-4">No apps found</h4>
    <p className="text-gray-500 mt-2">Try adjusting your filters or search term</p>
    <button
      onClick={resetFilters}
      className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
    >
      Clear All Filters
    </button>
  </div>
)}

      {/* App Detail Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl mt-5 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div
                    className="app-icon w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-sm"
                  >
                    {selectedApp.name ? selectedApp.name.charAt(0) : '?'}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedApp.name || 'Unknown App'}</h2>
                    <p className="text-sm text-gray-500 font-mono">{selectedApp.packageName || 'Unknown'}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedApp(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - App Information */}
                <div className="lg:col-span-1">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-4">App Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Risk Level</label>
                        <div className="mt-1">
                          <RiskBadge level={getRiskLevel(selectedApp.totalRiskScore || 0)} score={selectedApp.totalRiskScore || 0} />
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-500">Developer</label>
                        <p className="text-gray-900">{selectedApp.developer || 'Unknown'}</p>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-500">Category</label>
                        <p className="text-gray-900">{selectedApp.category || 'Unknown'}</p>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-500">Version</label>
                        <p className="text-gray-900">{selectedApp.version || 'N/A'}</p>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-500">Size</label>
                        <p className="text-gray-900">{selectedApp.size || 'N/A'}</p>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-500">Installs</label>
                        <p className="text-gray-900">{selectedApp.installs || 'N/A'}</p>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-500">Last Updated</label>
                        <p className="text-gray-900">{formatDate(selectedApp.lastUpdated)}</p>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-500">Android Version</label>
                        <p className="text-gray-900">Min: {selectedApp.minAndroidVersion || 'N/A'}</p>
                        <p className="text-gray-900">Target: {selectedApp.targetAndroidVersion || 'N/A'}</p>
                      </div>

                      {selectedApp.installSource && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Install Source</label>
                          <p className="text-gray-900">{selectedApp.installSource}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Column - Risk Analysis */}
                <div className="lg:col-span-2">
                  {/* Score Breakdown */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h3 className="text-lg font-semibold mb-4">Risk Score Breakdown</h3>
                    <div className="space-y-4">
                      <ProgressBar 
                        value={selectedApp.permissionsScore || 0} 
                        max={100} 
                        color="#3b82f6" 
                        label="Permission Score"
                      />
                      <ProgressBar 
                        value={selectedApp.cveScore || 0} 
                        max={100} 
                        color="#ef4444" 
                        label="CVE Score"
                      />
                      <ProgressBar 
                        value={selectedApp.totalRiskScore || 0} 
                        max={100} 
                        color={getRiskColor(getRiskLevel(selectedApp.totalRiskScore || 0))} 
                        label="Overall Risk Score"
                      />
                    </div>
                  </div>

                  {/* CVE Breakdown */}
                  {selectedApp.CVECounts && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <h3 className="text-lg font-semibold mb-4">CVE Breakdown</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-red-50 p-3 rounded-lg text-center">
                          <span className="text-xs text-gray-600">Critical</span>
                          <p className="text-2xl font-bold text-red-600">{selectedApp.CVECounts.critical || 0}</p>
                        </div>
                        <div className="bg-orange-50 p-3 rounded-lg text-center">
                          <span className="text-xs text-gray-600">High</span>
                          <p className="text-2xl font-bold text-orange-600">{selectedApp.CVECounts.high || 0}</p>
                        </div>
                        <div className="bg-yellow-50 p-3 rounded-lg text-center">
                          <span className="text-xs text-gray-600">Medium</span>
                          <p className="text-2xl font-bold text-yellow-600">{selectedApp.CVECounts.medium || 0}</p>
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg text-center">
                          <span className="text-xs text-gray-600">Low</span>
                          <p className="text-2xl font-bold text-green-600">{selectedApp.CVECounts.low || 0}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  {selectedApp.description && (
                    <div className="bg-blue-50 rounded-lg p-4 mb-4">
                      <h3 className="text-lg font-semibold mb-2">Description</h3>
                      <p className="text-gray-700">{selectedApp.description}</p>
                    </div>
                  )}

                  {/* Permissions */}
                  {selectedApp.permissions && selectedApp.permissions.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <h3 className="text-lg font-semibold mb-4">
                        Permissions ({selectedApp.permissions.length})
                        {selectedApp.criticalPermissions && selectedApp.criticalPermissions.length > 0 && (
                          <span className="ml-2 text-sm text-red-600">
                            ({selectedApp.criticalPermissions.length} critical)
                          </span>
                        )}
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {selectedApp.permissions.map(perm => {
                          const permInfo = PERMISSION_RISK_SCORES[perm];
                          const isCritical = permInfo?.score >= CRITICAL_PERMISSION_THRESHOLD;
                          return (
                            <div 
                              key={perm} 
                              className={`p-3 rounded-lg ${
                                isCritical ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-200'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-medium ${isCritical ? 'text-red-700' : ''}`}>
                                  {perm}
                                </span>
                                {isCritical && (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-red-500">
                                    <path d="M12 9V13M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                )}
                              </div>
                              {permInfo && (
                                <p className="text-xs text-gray-500 mt-1">{permInfo.description}</p>
                              )}
                              {permInfo && (
                                <p className="text-xs mt-1" style={{ color: getRiskColor(
                                  permInfo.score >= 8 ? 'critical' :
                                  permInfo.score >= 6 ? 'high' :
                                  permInfo.score >= 4 ? 'medium' : 'low'
                                ) }}>
                                  Risk Score: {permInfo.score}/10
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* CVEs */}
                  {selectedApp.cves && selectedApp.cves.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-lg font-semibold mb-4">Vulnerabilities ({selectedApp.cves.length})</h3>
                      <div className="space-y-3">
                        {selectedApp.cves.map(cve => (
                          <div key={cve.id} className="p-4 bg-white rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                              <div className="flex items-center gap-2">
                                <CVESeverityBadge severity={cve.severity} score={cve.score} cveScore={cve.cveScore} />
                                {cve.exploitAvailable && (
                                  <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full font-medium">
                                    Exploit Available
                                  </span>
                                )}
                                {cve.fixAvailable && (
                                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                                    Fix Available
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-gray-500">Published: {formatDate(cve.publishedDate)}</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{cve.description}</p>
                            {cve.cvssVector && (
                              <p className="text-xs text-gray-500 font-mono">CVSS: {cve.cvssVector}</p>
                            )}
                            {cve.id && cve.id.startsWith('CVE-') && (
                              <p className="text-xs font-mono text-blue-600 mt-1">ID: {cve.id}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setSelectedApp(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                {(getRiskLevel(selectedApp.totalRiskScore || 0) === 'critical' || getRiskLevel(selectedApp.totalRiskScore || 0) === 'high') && (
                  <button
                    onClick={() => {
                      handleAppAction('uninstall', selectedApp.appId);
                      setSelectedApp(null);
                    }}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    Uninstall App
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppRiskAnalysis;